import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Select,
  MultiSelect,
  Button,
  Group,
  Loader,
  Box,
  Badge,
  Divider,
  ScrollArea,
  Grid,
  Card,
  Alert,
  Checkbox,
  Tooltip,
  ActionIcon
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import {
  IconCopy,
  IconCheck,
  IconX,
  IconRefresh,
  IconInfoCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useRooms } from '../../hooks/coordinator/useBuildingManagement';
import { useCopyAssignments, usePreviewCopy, useAvailableDates } from '../../hooks/coordinator/useCopyRecurrence';
import { api } from '../../api';

/**
 * Copy Modal Component
 * 
 * Features from UX Flow Spec:
 * - Select scope (Day/Week/Month)
 * - Select source + multiple targets
 * - Options: overwrite / only vacancies
 * - Preview table with conflicts flagged
 * - Apply button with confirmation
 * - Real-time conflict detection
 */

export type CopyScope = 'day' | 'week' | 'month';

interface CopyModalProps {
  opened: boolean;
  onClose: () => void;
  sourceData?: {
    date: string;
    roomIds: string[];
  };
  loading?: boolean;
}


const CopyModal: React.FC<CopyModalProps> = ({
  opened,
  onClose,
  sourceData,
  loading = false
}) => {
  const [scope, setScope] = useState<string>('day');
  const [sourceDate, setSourceDate] = useState<string>(sourceData?.date || '');
  const [targetDates, setTargetDates] = useState<string[]>([]);
  const [filterRoomIds, setFilterRoomIds] = useState<string[]>(sourceData?.roomIds || []);
  const [options, setOptions] = useState({
    overwrite: false,
    onlyVacancies: true
  });
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assignmentCount, setAssignmentCount] = useState<number>(0);
  
  // =============================================================================
  // API HOOKS
  // =============================================================================
  
  const copyAssignmentsMutation = useCopyAssignments();
  const previewCopyMutation = usePreviewCopy();
  
  // Load real rooms data from API
  const { data: roomsData, isLoading: roomsLoading } = useRooms();
    
  // Load available dates with assignments
  // Load available dates immediately when modal opens (better UX)
  const { data: availableDatesData, isLoading: availableDatesLoading } = useAvailableDates({
    roomIds: [], // Empty array to get all rooms initially
    sessionTemplateIds: [],
    startDate: new Date().toISOString().split('T')[0], // Today
    endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // Next 90 days
  });

  const rooms = roomsData || [];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (opened) {
      setScope('day');
      // Don't pre-fill source date - let user select from available dates
      setSourceDate('');
      setTargetDates([]);
      setFilterRoomIds([]);
      setOptions({ overwrite: false, onlyVacancies: true });
      setPreviewData(null);
      setAssignmentCount(0);
    }
  }, [opened, sourceData]);
  
  // Fetch assignment count when source date or filter changes
  // Updated 15-10-2025 11:15:00 - Fix data access for unwrapped API response
  useEffect(() => {
    const fetchCount = async () => {
      if (!sourceDate) {
        setAssignmentCount(0);
        return;
      }
      
      try {
        // availableDatesData now contains: { availableDates: [...], totalDates, totalAssignments }
        if (availableDatesData?.availableDates) {
          const dateData = availableDatesData.availableDates.find((d: any) => d.date === sourceDate);
          
          if (!dateData) {
            setAssignmentCount(0);
            return;
          }
          
          // If room filter applied, filter actual assignments array for exact count
          if (filterRoomIds.length > 0) {
            const filteredAssignments = dateData.assignments.filter((a: any) => 
              filterRoomIds.includes(a.roomId)
            );
            setAssignmentCount(filteredAssignments.length);
          } else {
            // No filter = use total count from dateData
            setAssignmentCount(dateData.assignmentCount);
          }
        } else {
          setAssignmentCount(0);
        }
      } catch (error) {
        console.error('Error fetching assignment count:', error);
        setAssignmentCount(0);
      }
    };
    
    fetchCount();
  }, [sourceDate, filterRoomIds, availableDatesData, rooms.length]);

  // Generate target dates based on scope
  const generateTargetDates = (startDate: string, scope: string) => {
    if (!startDate) return [];
    
    const date = new Date(startDate);
    const dates: string[] = [];
    
    switch (scope) {
      case 'day':
        // Next 7 days
        for (let i = 1; i <= 7; i++) {
          const newDate = new Date(date);
          newDate.setDate(date.getDate() + i);
          dates.push(newDate.toISOString().split('T')[0]);
        }
        break;
      case 'week':
        // Next 4 weeks
        for (let i = 1; i <= 4; i++) {
          const newDate = new Date(date);
          newDate.setDate(date.getDate() + (i * 7));
          dates.push(newDate.toISOString().split('T')[0]);
        }
        break;
      case 'month':
        // Next 3 months
        for (let i = 1; i <= 3; i++) {
          const newDate = new Date(date);
          newDate.setMonth(date.getMonth() + i);
          dates.push(newDate.toISOString().split('T')[0]);
        }
        break;
    }
    
    return dates;
  };

  const handleScopeChange = (value: string | null) => {
    if (value) {
      setScope(value);
      const newTargetDates = generateTargetDates(sourceDate, value);
      setTargetDates(newTargetDates);
    }
  };

  const handlePreview = async () => {
    if (!sourceDate || targetDates.length === 0) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select source date and at least one target date',
        color: 'red'
      });
      return;
    }

    setPreviewLoading(true);
    try {
      // Call preview API with dry-run mode (no database writes)
      // Hook now returns unwrapped data directly: { potentialCopies, conflicts, ... }
      const result = await previewCopyMutation.mutateAsync({
        sourceDate,
        targetDates,
        roomIds: filterRoomIds.length > 0 ? filterRoomIds : undefined,
        sessionTemplateIds: undefined,
        options: {
          overwrite: options.overwrite,
          onlyVacancies: options.onlyVacancies
        }
      });

      // Transform API response for display
      // result is already unwrapped data payload (not envelope)
      const transformedPreview = {
        sourceAssignments: result.sourceAssignments || 0,
        potentialCopies: result.potentialCopies || 0,
        conflicts: result.conflicts || [],
        skippedCount: result.skippedCount || 0,
        totalAttempted: result.totalAttempted || 0,
        preview: result.conflicts?.map((conflict: any) => ({
          date: conflict.date,
          roomNumber: conflict.roomNumber,
          sessionName: conflict.sessionName,
          doctorName: conflict.doctorName,
          status: 'conflict',
          conflictCodes: conflict.conflicts?.map((c: any) => c.code) || [],
          conflictMessages: conflict.conflicts?.map((c: any) => c.message) || []
        })) || []
      };

      setPreviewData(transformedPreview);
      
      notifications.show({
        title: 'Preview Generated',
        message: `${result.potentialCopies || 0} assignments can be created, ${result.conflicts?.length || 0} conflicts detected`,
        color: result.conflicts?.length > 0 ? 'yellow' : 'green'
      });
    } catch (error: any) {
      notifications.show({
        title: 'Preview Failed',
        message: error?.message || 'Failed to generate preview. Please check your selections.',
        color: 'red'
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!sourceDate || targetDates.length === 0) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select source date and target dates',
        color: 'red'
      });
      return;
    }

    setSubmitting(true);
    try {
      // Prepare copy data for API
          const copyData = {
            sourceDate,
            targetDates,
            ...(filterRoomIds.length > 0 && { roomIds: filterRoomIds }),
            sessionTemplateIds: [], // TODO: Add session time selection
            options: {
              overwrite: options.overwrite,
              onlyVacancies: options.onlyVacancies
            }
          };

      // Call the real API (dryRun: false for actual execution)
      const result = await copyAssignmentsMutation.mutateAsync(copyData as any);
      
      // Show detailed success notification with undo option
      const message = result.conflicts?.length > 0 
        ? `Created ${result.copiedCount || 0} assignments, skipped ${result.skippedCount || 0} due to conflicts`
        : `Successfully created ${result.copiedCount || 0} assignments`;
      
      // Show detailed success notification
      notifications.show({
        title: 'Copy Operation Complete',
        message: (
          <Stack gap="sm">
            <Text size="sm">{message}</Text>
            {result.copiedCount > 0 && (
              <>
                <Divider />
                <Group justify="space-between" align="center">
                  <Text size="xs" c="dimmed">
                    Undo available for 5 minutes
                  </Text>
                  <Button 
                    size="xs" 
                    variant="subtle"
                    color="red"
                    onClick={async () => {
                      const confirmed = window.confirm(
                        `Delete ${result.copiedCount} assignments created by this copy operation?\n\nThis action cannot be undone.`
                      );
                      
                      if (confirmed && result.assignments?.length > 0) {
                        try {
                          // Delete all created assignments
                          await Promise.all(
                            result.assignments.map((assignment: any) =>
                              api.delete(`/assignments/${assignment.id}`)
                            )
                          );
                          
                          notifications.show({
                            title: 'Undo Successful',
                            message: `Deleted ${result.copiedCount} assignments`,
                            color: 'blue'
                          });
                          
                          // Refresh grid
                          copyAssignmentsMutation.reset();
                        } catch (error) {
                          notifications.show({
                            title: 'Undo Failed',
                            message: 'Failed to delete assignments. Please try manually.',
                            color: 'red'
                          });
                        }
                      }
                    }}
                  >
                    Undo Copy
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        ),
        color: result.conflicts?.length > 0 ? 'yellow' : 'green',
        icon: <IconCheck size={16} />,
        autoClose: result.copiedCount > 0 ? 5 * 60 * 1000 : 8000, // 5 minutes if undo available
        withCloseButton: true
      });
      
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Copy Failed',
        message: error instanceof Error ? error.message : 'Failed to copy assignments',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canPreview = sourceDate && targetDates.length > 0;
  const canSubmit = sourceDate && targetDates.length > 0 && !submitting && !loading && !copyAssignmentsMutation.isPending;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconCopy size={20} />
          <Text fw={600}>Copy Assignments</Text>
        </Group>
      }
      size={600}
      centered
      closeOnClickOutside={false}
      closeOnEscape={!submitting}
      trapFocus
      returnFocus
    >
      <Stack gap="md">
        {/* Guidance Alert */}
        {!sourceDate && !availableDatesLoading && (
          <Alert color="blue" title="How to Copy Assignments" icon={<IconInfoCircle size={16} />}>
            <Text size="sm">
              <strong>Step 1:</strong> Select a source date from the calendar below (green dates have assignments)
              <br />
              <strong>Step 2:</strong> Choose which rooms to copy from and to
              <br />
              <strong>Step 3:</strong> Preview conflicts before applying
              <br />
              <br />
              <Text size="xs" c="dimmed">
                💡 Tip: Only future dates with existing assignments can be selected. Past dates are read-only per hospital policy.
              </Text>
            </Text>
          </Alert>
        )}

        {/* No Available Dates Warning */}
        {!availableDatesLoading && availableDatesData?.availableDates?.length === 0 && (
          <Alert color="yellow" title="No Assignments Available to Copy" icon={<IconInfoCircle size={16} />}>
            <Text size="sm">
              There are no future assignments available to copy. You need to create some assignments first before using the copy feature.
              <br />
              <br />
              <Text size="xs" c="dimmed">
                💡 Tip: Create assignments by clicking on empty cells in the planner grid, then come back to copy them.
              </Text>
            </Text>
          </Alert>
        )}

        {/* Scope Selection */}
        <Box>
          <Group gap="xs" mb="xs">
            <Text size="sm" fw={500}>Copy Scope *</Text>
            <Tooltip 
              label={`WHY: Copy assignments to multiple dates efficiently without manual repetition

WHAT:
• Day: Creates copies for the next 7 consecutive days
• Week: Creates copies for the next 4 weeks (same weekday each week)
• Month: Creates copies for the next 3 months (same date each month)

WHEN TO USE:
• Day: Short-term coverage (vacation coverage, temporary schedules)
• Week: Regular weekly patterns (doctor works same day each week)
• Month: Long-term planning (recurring monthly duties)`}
              multiline
              w={450}
              position="right"
              withArrow
            >
              <ActionIcon size="xs" variant="subtle" color="blue" aria-label="Scope selection help">
                <IconInfoCircle size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Select
            placeholder="Select copy scope"
            data={[
              { value: 'day', label: 'Day (Next 7 days)' },
              { value: 'week', label: 'Week (Next 4 weeks)' },
              { value: 'month', label: 'Month (Next 3 months)' }
            ]}
            value={scope}
            onChange={handleScopeChange}
            disabled={loading || submitting}
            required
          />
        </Box>

        <Grid>
          {/* Source Selection */}
          <Grid.Col span={6}>
            <Box>
              <Group gap="xs" mb="xs">
                <Text size="sm" fw={500}>Source Date *</Text>
                <Tooltip 
                  label={`WHY: Select a date that has assignments to copy FROM

WHAT: Calendar shows only FUTURE dates with existing assignments
• System automatically filters out past dates (7 Hard Rules #7)
• Only dates with confirmed assignments are selectable

VISUAL INDICATORS:
• Green background + Blue dot: Date has assignments available to copy
• Grayed out: Past dates (cannot be selected - read-only)
• Faded: No assignments on this date

HOW IT WORKS:
1. Calendar loads showing all future dates with assignments
2. Hover over green dates to see assignment count
3. Select a date to see which assignments will be copied
4. System validates date has assignments before proceeding

IMPORTANT: If you don't see any green dates, it means there are no future assignments available to copy. You'll need to create some assignments first.`}
                  multiline
                  w={500}
                  position="right"
                  withArrow
                >
                  <ActionIcon size="xs" variant="subtle" color="blue" aria-label="Source date help">
                    <IconInfoCircle size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              {availableDatesLoading && (
                <Text size="sm" c="dimmed" mb="xs">
                  Loading available dates...
                </Text>
              )}
              <DatePicker
                type="default"
                value={sourceDate ? new Date(sourceDate) : null}
                onChange={(date) => {
                  if (date) {
                    const dateString = date.toISOString().split('T')[0];
                    setSourceDate(dateString);
                    // Regenerate target dates when source date changes
                    const newTargetDates = generateTargetDates(dateString, scope);
                    setTargetDates(newTargetDates);
                  }
                }}
                minDate={new Date()}
                renderDay={(date) => {
                  const dateString = date.toISOString().split('T')[0];
                  
                  // Find the date data from API response
                  const dateData = availableDatesData?.availableDates?.find(
                    (availableDate: any) => availableDate.date === dateString
                  );
                  
                  const hasAssignments = !!dateData;
                  const assignmentCount = dateData?.assignmentCount || 0;
                  
                  // Use backend's isPast determination (authoritative business logic)
                  // Backend handles timezone, business rules, and past session locking (7 Hard Rules #7)
                  const isPast = dateData?.isPast ?? true; // Default to past if no data
                  
                  return (
                    <Tooltip
                      label={
                        isPast 
                          ? "Past dates cannot be selected (7 Hard Rules #7: Past Session Lock)"
                          : hasAssignments 
                            ? `${assignmentCount} assignment${assignmentCount === 1 ? '' : 's'} available to copy`
                            : "No assignments available on this date"
                      }
                      position="top"
                      withArrow
                    >
                      <div style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: hasAssignments && !isPast ? 'pointer' : 'not-allowed',
                        opacity: isPast ? 0.3 : hasAssignments ? 1 : 0.5,
                        backgroundColor: hasAssignments && !isPast ? '#e7f5e7' : 'transparent',
                        borderRadius: '4px'
                      }}>
                        {date.getDate()}
                        {hasAssignments && !isPast && (
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            right: '2px',
                            width: '6px',
                            height: '6px',
                            backgroundColor: '#228be6',
                            borderRadius: '50%'
                          }} />
                        )}
                      </div>
                    </Tooltip>
                  );
                }}
                getDayProps={(date) => {
                  const dateString = date.toISOString().split('T')[0];
                  
                  // Find the date data from API response
                  const dateData = availableDatesData?.availableDates?.find(
                    (availableDate: any) => availableDate.date === dateString
                  );
                  
                  const hasAssignments = !!dateData;
                  
                  // Use backend's isPast determination (authoritative business logic)
                  // Backend handles timezone, business rules, and past session locking
                  const isPast = dateData?.isPast ?? true; // Default to past if no data
                  
                  return {
                    disabled: loading || submitting || availableDatesLoading || isPast || !hasAssignments,
                  };
                }}
              />
              
              {/* Assignment Count Display */}
              {sourceDate && (
                <Card withBorder p="md" mt="md" bg="blue.0">
                  <Group justify="space-between" align="center">
                    <div>
                      <Text size="sm" fw={500}>Assignments Found</Text>
                      <Text size="xs" c="dimmed">On {sourceDate}</Text>
                    </div>
                    <Badge size="xl" variant="filled" color={assignmentCount > 0 ? 'blue' : 'gray'}>
                      {assignmentCount}
                    </Badge>
                  </Group>
                  {assignmentCount > 0 && targetDates.length > 0 && (
                    <>
                      <Divider my="sm" />
                      <Text size="xs" c="dimmed">
                        Will create <Text component="span" fw={600}>{assignmentCount * targetDates.length}</Text> assignments
                        ({assignmentCount} source × {targetDates.length} target date{targetDates.length !== 1 ? 's' : ''})
                      </Text>
                    </>
                  )}
                  {assignmentCount === 0 && (
                    <>
                      <Divider my="sm" />
                      <Text size="xs" c="orange">
                        No assignments found on this date. Please select a different date.
                      </Text>
                    </>
                  )}
                </Card>
              )}
            </Box>
          </Grid.Col>
          <Grid.Col span={6}>
            <Box>
              <Group gap="xs" mb="xs">
                <Text size="sm" fw={500}>Target Dates</Text>
                <Tooltip 
                  label={`WHY: Define which dates to copy assignments TO

WHAT: Target dates are automatically generated based on scope:
• Day scope: Next 7 consecutive days
• Week scope: Next 4 weeks (same weekday)
• Month scope: Next 3 months (same date)

VALIDATION: Past dates automatically excluded (cannot copy to past)
System validates each target date against 7 Hard Rules

HOLIDAYS: Assignments on holidays may be skipped if system detects conflicts`}
                  multiline
                  w={420}
                  position="left"
                  withArrow
                >
                  <ActionIcon size="xs" variant="subtle" color="blue" aria-label="Target dates help">
                    <IconInfoCircle size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Card withBorder p="xs" bg="gray.0">
                <Group gap="xs">
                  <Badge color="blue" variant="dot">
                {targetDates.length} dates selected
                  </Badge>
                  {targetDates.length > 0 && (
                    <Text size="xs" c="dimmed">
                      {targetDates[0]} to {targetDates[targetDates.length - 1]}
              </Text>
                  )}
                </Group>
              </Card>
            </Box>
          </Grid.Col>
        </Grid>

        <Grid>
          {/* Optional Room Filtering */}
          <Grid.Col span={6}>
            <Box>
              <Group gap="xs" mb="xs">
                <Text size="sm" fw={500}>Filter by Rooms (Optional)</Text>
                <Tooltip 
                  label={`OPTIONAL FILTER: Narrow down which assignments to copy

WHAT: Leave empty to copy ALL assignments from source date
Select specific rooms to copy only those rooms' assignments

EXAMPLE:
• Empty selection = Copy all rooms (e.g., 20 assignments from 10 rooms)
• Select Room A101 = Copy only Room A101's assignments (e.g., 2 assignments)

IMPACT: Filtered assignments are duplicated to target dates
Each copied assignment keeps the same room, doctor, and session
Only the date changes (temporal duplication, not cross-room transfer)`}
                  multiline
                  w={420}
                  position="right"
                  withArrow
                >
                  <ActionIcon size="xs" variant="subtle" color="blue" aria-label="Filter rooms help">
                    <IconInfoCircle size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <MultiSelect
                placeholder={roomsLoading ? "Loading rooms..." : "Leave empty to copy all rooms"}
                data={rooms.map(room => ({
                  value: room.id,
                  label: `${room.number} - ${room.name}${room.capacity ? ` (Capacity: ${room.capacity})` : ''}`
                }))}
                value={filterRoomIds}
                onChange={(values) => setFilterRoomIds(values || [])}
                disabled={loading || submitting || roomsLoading}
                maxValues={5}
                searchable
                clearable
              />
            </Box>
          </Grid.Col>
        </Grid>

        {/* Options */}
        <Box>
          <Text size="sm" fw={500} mb="xs">Copy Options</Text>
          <Stack gap="xs">
            <Group gap="xs">
            <Checkbox
              label="Overwrite existing assignments"
              checked={options.overwrite}
              onChange={(e) => setOptions({ ...options, overwrite: e.currentTarget.checked })}
              disabled={loading || submitting}
            />
              <Tooltip 
                label={`WHY: Control behavior when target slot already has an assignment

WHAT:
• Enabled: Replaces existing assignments with copied ones (CAUTION REQUIRED)
• Disabled: Skips occupied slots, preserves existing assignments

IMPACT: Overwriting deletes original assignments and replaces with copies

RECOMMENDED: Keep disabled to prevent accidental data loss
Use only when intentionally replacing incorrect assignments`}
                multiline
                w={400}
                position="right"
                withArrow
              >
                <ActionIcon size="xs" variant="subtle" color="blue" aria-label="Overwrite option help">
                  <IconInfoCircle size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
            
            <Group gap="xs">
            <Checkbox
              label="Only copy to vacant slots"
              checked={options.onlyVacancies}
              onChange={(e) => setOptions({ ...options, onlyVacancies: e.currentTarget.checked })}
              disabled={loading || submitting}
            />
              <Tooltip 
                label={`WHY: Ensure you don't exceed room capacity limits (7 Hard Rules #1)

WHAT:
• Enabled: Only copies to slots with available capacity
• Disabled: Attempts all copies (may trigger capacity conflicts)

VALIDATION: System checks room capacity before creating each assignment
If capacity exceeded, assignment is skipped and flagged in conflicts

RECOMMENDED: Enable to automatically respect capacity limits
Disable only if you want to see all potential conflicts in preview`}
                multiline
                w={420}
                position="right"
                withArrow
              >
                <ActionIcon size="xs" variant="subtle" color="blue" aria-label="Only vacancies option help">
                  <IconInfoCircle size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Stack>
        </Box>

        {/* Preview Section - Enhanced with Conflict Visualization */}
        {previewData && (
          <Box>
            <Divider my="md" />
            <Group justify="space-between" mb="md">
              <Text fw={600}>Preview Results</Text>
              <Group gap="xs">
                <Badge color="green" variant="dot">
                  {previewData.potentialCopies} Can Create
                </Badge>
                {previewData.conflicts?.length > 0 && (
                  <Badge color="red" variant="dot">
                    {previewData.skippedCount} Conflicts
              </Badge>
                )}
              </Group>
            </Group>
            
            {/* Summary Statistics */}
            <Card withBorder p="sm" mb="md" bg="gray.0">
              <Group justify="space-between">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed">Total Attempts</Text>
                  <Text fw={600}>{previewData.totalAttempted}</Text>
                </Stack>
                <Stack gap={4}>
                  <Text size="xs" c="dimmed">Will Create</Text>
                  <Text fw={600} c="green">{previewData.potentialCopies}</Text>
                </Stack>
                <Stack gap={4}>
                  <Text size="xs" c="dimmed">Conflicts</Text>
                  <Text fw={600} c="red">{previewData.skippedCount}</Text>
                </Stack>
              </Group>
            </Card>
            
            {/* Conflict Details */}
            {previewData.conflicts?.length > 0 && (
              <Box>
                <Text size="sm" fw={500} mb="xs" c="red">Conflicts Detected:</Text>
                <ScrollArea h={250}>
              <Stack gap="xs">
                    {previewData.preview.map((conflict: any, index: number) => (
                      <Card key={index} withBorder p="sm" bg="red.0">
                    <Stack gap="xs">
                      <Group justify="space-between">
                            <Text size="sm" fw={500}>{conflict.doctorName || 'Doctor'}</Text>
                            <Badge color="red" size="sm">Conflict</Badge>
                      </Group>
                          <Text size="xs" c="dimmed">
                            Room {conflict.roomNumber} • {conflict.sessionName} • {conflict.date}
                          </Text>
                          <Divider />
                          {conflict.conflictCodes?.map((code: string, cIndex: number) => (
                            <Tooltip 
                              key={cIndex}
                              label={conflict.conflictMessages?.[cIndex] || code}
                              multiline
                              w={300}
                            >
                              <Badge color="red" variant="light" size="sm" style={{ cursor: 'help' }}>
                                {code}
                          </Badge>
                            </Tooltip>
                      ))}
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </ScrollArea>
              </Box>
            )}
            
            {/* Success Message if No Conflicts */}
            {previewData.conflicts?.length === 0 && (
              <Card withBorder p="md" bg="green.0">
                <Group>
                  <IconCheck size={20} color="green" />
                  <Text c="green" fw={500}>
                    All {previewData.potentialCopies} assignments can be created successfully with no conflicts!
                  </Text>
                </Group>
              </Card>
            )}
          </Box>
        )}

        {/* Loading State */}
        {(previewLoading || loading) && (
          <Box ta="center" py="md">
            <Loader size="sm" />
            <Text size="sm" c="dimmed" mt="xs">
              {previewLoading ? 'Generating preview...' : 'Processing...'}
            </Text>
          </Box>
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="light"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Tooltip 
            label={`WHY: See conflicts before committing changes to the schedule

WHAT: System validates all target slots against 7 Hard Rules without creating assignments

RESULT:
• Green: Assignments will be created successfully
• Red: Conflicts detected (capacity, double-booking, blocks, etc.)
• Shows detailed conflict codes and reasons

ACTION: Always preview first, review conflicts, adjust selections if needed, then Apply`}
            multiline
            w={420}
            position="top"
            withArrow
            disabled={!canPreview}
          >
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={!canPreview || previewLoading || submitting}
            leftSection={<IconRefresh size={16} />}
          >
              {previewLoading ? 'Checking...' : 'Preview'}
          </Button>
          </Tooltip>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting || copyAssignmentsMutation.isPending}
            leftSection={<IconCopy size={16} />}
          >
            {(submitting || copyAssignmentsMutation.isPending) ? 'Copying...' : 'Copy Assignments'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CopyModal;